import { z } from 'zod';
import { EQUIP_SLOTS } from '@jianghu/game-data';

/** Client → server messages. Every message is validated with these schemas before it is processed. */

const dir = z.number().min(-1.01).max(1.01);
const entityId = z
  .number()
  .int()
  .positive()
  .max(2 ** 31);
const id64 = z.string().min(1).max(64);
const bagSlot = z.number().int().min(0).max(255);

export const CastActionSchema = z.object({
  sk: id64,
  tg: entityId.optional(),
  ax: dir,
  az: dir,
});

/** One fixed-rate (30 Hz) movement command, optionally carrying a technique activation. */
export const InputMsgSchema = z.object({
  t: z.literal('in'),
  s: z
    .number()
    .int()
    .nonnegative()
    .max(2 ** 31),
  mx: dir,
  mz: dir,
  sp: z.boolean(),
  dg: z.boolean(),
  a: CastActionSchema.optional(),
});

export const PingMsgSchema = z.object({ t: z.literal('ping'), c: z.number() });
export const PickupMsgSchema = z.object({ t: z.literal('pickup'), id: entityId });
export const NpcMsgSchema = z.object({
  t: z.literal('npc'),
  npc: entityId,
  op: z.enum(['talk', 'accept', 'turnin', 'shop']),
  q: id64.optional(),
});
export const InvMsgSchema = z.discriminatedUnion('op', [
  z.object({ t: z.literal('inv'), op: z.literal('equip'), item: id64 }),
  z.object({ t: z.literal('inv'), op: z.literal('unequip'), slot: z.enum(EQUIP_SLOTS) }),
  z.object({ t: z.literal('inv'), op: z.literal('move'), from: bagSlot, to: bagSlot }),
  z.object({
    t: z.literal('inv'),
    op: z.literal('split'),
    slot: bagSlot,
    qty: z.number().int().positive().max(1000),
  }),
  z.object({ t: z.literal('inv'), op: z.literal('discard'), item: id64 }),
  z.object({ t: z.literal('inv'), op: z.literal('use'), item: id64 }),
]);
export const ShopMsgSchema = z.discriminatedUnion('op', [
  z.object({
    t: z.literal('shop'),
    npc: entityId,
    op: z.literal('buy'),
    item: id64,
    qty: z.number().int().positive().max(100),
  }),
  z.object({
    t: z.literal('shop'),
    npc: entityId,
    op: z.literal('sell'),
    item: id64,
    qty: z.number().int().positive().max(1000),
  }),
]);
export const ChatMsgSchema = z.object({ t: z.literal('chat'), text: z.string().min(1).max(200) });
export const TeleportMsgSchema = z.object({ t: z.literal('tp'), to: id64 });
export const RespawnMsgSchema = z.object({ t: z.literal('respawn') });
export const QuestMsgSchema = z.object({ t: z.literal('quest'), op: z.literal('abandon'), q: id64 });

const SCHEMAS = {
  in: InputMsgSchema,
  ping: PingMsgSchema,
  pickup: PickupMsgSchema,
  npc: NpcMsgSchema,
  inv: InvMsgSchema,
  shop: ShopMsgSchema,
  chat: ChatMsgSchema,
  tp: TeleportMsgSchema,
  respawn: RespawnMsgSchema,
  quest: QuestMsgSchema,
} as const;

export type ClientMessageType = keyof typeof SCHEMAS;
export type ClientMessage = { [K in ClientMessageType]: z.infer<(typeof SCHEMAS)[K]> }[ClientMessageType];
export type InputMsg = z.infer<typeof InputMsgSchema>;
export type CastAction = z.infer<typeof CastActionSchema>;
export type InvMsg = z.infer<typeof InvMsgSchema>;
export type ShopMsg = z.infer<typeof ShopMsgSchema>;
export type NpcMsg = z.infer<typeof NpcMsgSchema>;

/** Maximum accepted size of one client WebSocket frame (bytes). */
export const MAX_CLIENT_MESSAGE_BYTES = 2048;

export type ParseResult = { ok: true; msg: ClientMessage } | { ok: false; error: string };

/** Parses and validates one raw client frame. Never throws. */
export function parseClientMessage(raw: string): ParseResult {
  if (raw.length > MAX_CLIENT_MESSAGE_BYTES) return { ok: false, error: 'too_large' };
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return { ok: false, error: 'bad_json' };
  }
  if (typeof data !== 'object' || data === null || Array.isArray(data))
    return { ok: false, error: 'not_object' };
  const t = (data as { t?: unknown }).t;
  if (typeof t !== 'string' || !Object.hasOwn(SCHEMAS, t)) return { ok: false, error: 'unknown_type' };
  const schema = SCHEMAS[t as ClientMessageType];
  const r = schema.safeParse(data);
  if (!r.success) return { ok: false, error: `invalid_${t}` };
  return { ok: true, msg: r.data as ClientMessage };
}
